import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function MissionPage() {
  const { t } = useTranslation();

  const values = [
    { icon: "ri-heart-line", title: t("missionPage.values.compassion"), desc: t("missionPage.values.compassionDesc") },
    { icon: "ri-shield-star-line", title: t("missionPage.values.integrity"), desc: t("missionPage.values.integrityDesc") },
    { icon: "ri-team-line", title: t("missionPage.values.collaboration"), desc: t("missionPage.values.collaborationDesc") },
    { icon: "ri-seedling-line", title: t("missionPage.values.sustainability"), desc: t("missionPage.values.sustainabilityDesc") },
    { icon: "ri-scales-line", title: t("missionPage.values.equity"), desc: t("missionPage.values.equityDesc") },
    { icon: "ri-lightbulb-line", title: t("missionPage.values.innovation"), desc: t("missionPage.values.innovationDesc") },
  ];

  const timeline = [
    { year: "2016", event: t("missionPage.timeline.2016") },
    { year: "2017", event: t("missionPage.timeline.2017") },
    { year: "2018", event: t("missionPage.timeline.2018") },
    { year: "2019", event: t("missionPage.timeline.2019") },
    { year: "2020", event: t("missionPage.timeline.2020") },
    { year: "2021", event: t("missionPage.timeline.2021") },
    { year: "2022", event: t("missionPage.timeline.2022") },
    { year: "2023", event: t("missionPage.timeline.2023") },
    { year: "2024", event: t("missionPage.timeline.2024") },
  ];

  return (
    <main className="pt-24">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Afghan%20landscape%20with%20mountains%20and%20a%20village%2C%20golden%20hour%20warm%20light%2C%20peaceful%20and%20hopeful%20atmosphere%2C%20wide%20panoramic%20view%20with%20earth%20tones%20and%20greens&width=1600&height=600&seq=mission1&orientation=landscape"
            alt="Mission"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#1B2838]/75"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-4 block">{t("missionPage.tag")}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("missionPage.title")}
          </h1>
          <p className="text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
            {t("missionPage.subtitle")}
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">{t("missionPage.who.tag")}</span>
              <h2 className="text-3xl font-bold text-[#1B2838] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t("missionPage.who.title")}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                {t("missionPage.who.body1")}
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                {t("missionPage.who.body2")}
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                {t("missionPage.who.body3")}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/projects" className="px-6 py-3 bg-[#1B2838] text-white rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-[#2a3a4a] transition-colors">
                  {t("missionPage.who.ctaProjects")}
                </Link>
                <Link to="/donate" className="px-6 py-3 border-2 border-[#1B2838] text-[#1B2838] rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-[#1B2838] hover:text-white transition-all">
                  {t("missionPage.who.ctaSupport")}
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Afghan%20community%20members%20and%20Canadian%20volunteers%20working%20together%20on%20a%20development%20project%2C%20collaborative%20and%20warm%20atmosphere%2C%20natural%20lighting%2C%20diverse%20group%20of%20people&width=700&height=500&seq=mission2&orientation=landscape"
                alt="CAF Team"
                className="w-full h-56 sm:h-72 md:h-80 object-cover object-top rounded-xl"
              />
              <div className="absolute -bottom-5 -left-5 bg-[#4ECDC4] text-[#1B2838] rounded-xl p-5 font-bold">
                <div className="text-3xl">8+</div>
                <div className="text-sm">{t("missionPage.who.yearsBadge")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">{t("missionPage.values.tag")}</span>
            <h2 className="text-3xl font-bold text-[#1B2838]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("missionPage.values.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-[#4ECDC4]/15 text-[#4ECDC4] mb-4">
                  <i className={`${v.icon} text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1B2838] text-base mb-2">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">{t("missionPage.timeline.tag")}</span>
            <h2 className="text-3xl font-bold text-[#1B2838]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("missionPage.timeline.title")}
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-[72px] md:left-[88px] top-0 bottom-0 w-0.5 bg-gray-200"></div>
            <div className="space-y-8">
              {timeline.map((item, i) => (
                <div key={i} className="flex items-start gap-4 md:gap-6">
                  <div className="w-12 md:w-14 text-right flex-shrink-0">
                    <span className="text-sm font-bold text-[#4ECDC4]">{item.year}</span>
                  </div>
                  <div className="relative flex-shrink-0 mt-1">
                    <div className="w-4 h-4 rounded-full bg-[#4ECDC4] border-2 border-white ring-2 ring-[#4ECDC4]/30"></div>
                  </div>
                  <div className="flex-1 pb-2">
                    <p className="text-gray-700 text-sm leading-relaxed">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1B2838]">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("missionPage.cta.title")}
          </h2>
          <p className="text-gray-400 mb-8">
            {t("missionPage.cta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donate" className="px-8 py-4 bg-[#4ECDC4] text-[#1B2838] rounded-full font-bold whitespace-nowrap cursor-pointer hover:bg-[#3dbdb5] transition-colors">
              {t("missionPage.cta.donate")}
            </Link>
            <Link to="/volunteer" className="px-8 py-4 border border-white/30 text-white rounded-full font-semibold whitespace-nowrap cursor-pointer hover:bg-white/10 transition-colors">
              {t("missionPage.cta.volunteer")}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}